import { useCallback, useEffect, useRef, useState } from 'react'

interface UseTimerReturn {
  timeLeft: number
  isRunning: boolean
  start: () => void
  stop: () => void
  reset: () => void
}

const useTimer = (durationInMs: number): UseTimerReturn => {
  const [timeLeft, setTimeLeft] = useState(durationInMs)
  const [isRunning, setIsRunning] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number | null>(null)

  const start = useCallback(() => {
    if (isRunning) return

    setIsRunning(true)
    startTimeRef.current = Date.now()

    intervalRef.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        const elapsed = Date.now() - (startTimeRef.current || 0)
        const remaining = Math.max(0, durationInMs - elapsed)

        if (remaining <= 0) {
          setIsRunning(false)
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          return 0
        }

        return remaining
      })
    }, 1000) // Update every 1 second instead of 100ms for better performance
  }, [durationInMs, isRunning])

  const stop = useCallback(() => {
    setIsRunning(false)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const reset = useCallback(() => {
    stop()
    setTimeLeft(durationInMs)
    startTimeRef.current = null
  }, [durationInMs, stop])

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  return {
    timeLeft,
    isRunning,
    start,
    stop,
    reset,
  }
}

export default useTimer
